'use client';

import { useEffect, useState, useCallback, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { toast } from '@/lib/toast';
import {
  type UserProfile,
  type LawFirmProfile,
  type UpdateUserProfileRequest,
  type UpdateLawFirmProfileRequest,
  type ProfileUpdateEvent,
  type DismissedHelpBanners
} from './types';
import {
  getUserProfileAction,
  updateUserProfileAction,
  uploadProfilePhotoAction
} from './actions';
import { updateUserPreferenceAction } from '@/lib/services/account';
import { profileKeys } from '@/lib/query-keys/profile';
import { getDependencies } from '@/lib/services/query-dependencies';
import {
  getCachedProfile,
  setCachedProfile,
  clearCachedProfile,
  getUserDisplayName,
  getUserInitials
} from './utils';

// Placeholder law-firm profile. The law-firm profile is not a separate
// resource in this implementation yet, so `useLawFirmProfile` returns this
// stub. (Previously sourced from the removed mock-data fixture; inlined here
// as the real-path default until a dedicated endpoint exists.)
const mockLawFirmProfile: LawFirmProfile = {
  id: '1',
  name: 'Vale Legal Services',
  email: 'contact@valelegal.com.au',
  phone: '+61 2 9123 4567',
  website: 'https://valelegal.com.au',
  abn: '12 345 678 901',
  logo: '/logos/vale-legal.png',
  primaryContact: {
    fullName: 'Sarah Johnson',
    email: 'sarah.johnson@valelegal.com',
    phone: '+61 2 9123 4567',
    title: 'Senior Solicitor',
    department: 'Probate & Estate Administration'
  },
  postalAddress: {
    street: 'Level 15, 123 Collins Street',
    suburb: 'Sydney',
    state: 'NSW',
    postcode: '2000',
    country: 'Australia'
  },
  businessAddress: {
    street: 'Level 15, 123 Collins Street',
    suburb: 'Sydney',
    state: 'NSW',
    postcode: '2000',
    country: 'Australia'
  },
  correspondencePreferences: {
    replyToOption: 'firm',
    showUserContact: true,
    emailFooter:
      'This email and any attachments are confidential and legally privileged.',
    autoCC: ['admin@smithlaw.com.au']
  },
  trustBankAccount: {
    accountName: 'Smith & Associates Trust Account',
    accountNumber: '123456789',
    bsb: '062-123',
    bankName: 'Commonwealth Bank of Australia',
    branch: 'Sydney CBD'
  },
  subscription: {
    plan: 'professional',
    status: 'active',
    currentPeriodEnd: '2024-12-31T23:59:59Z',
    seats: 10,
    features: ['unlimited_matters', 'advanced_reporting', 'api_access']
  },
  verifiedAt: '2023-06-20T10:00:00Z',
  createdAt: '2023-06-15T09:00:00Z',
  updatedAt: '2024-01-15T14:30:00Z'
};

// ============================================================================
// User Profile Hook
// ============================================================================

interface UseUserProfileOptions {
  /**
   * Whether to fetch the profile immediately on mount
   * @default true
   */
  fetchOnMount?: boolean;

  /**
   * Whether to use cached data if available
   * @default true
   */
  useCache?: boolean;

  /**
   * Cache duration in milliseconds
   * @default 300000 (5 minutes)
   */
  cacheDuration?: number;

  /**
   * Callback when profile is successfully loaded
   */
  onSuccess?: (profile: UserProfile) => void;

  /**
   * Callback when profile loading fails
   */
  onError?: (error: string) => void;
}

export function useUserProfile(options: UseUserProfileOptions = {}) {
  const { fetchOnMount = true, onSuccess, onError } = options;

  const router = useRouter();
  const queryClient = useQueryClient();
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [isMutating, setIsMutating] = useState(false);

  // All `/profile` reads flow through the shared canonical query so
  // TanStack Query deduplicates fetches across providers (the dashboard
  // used to trigger 3 separate fetches on mount).
  const {
    data: profileResponse,
    isLoading: isQueryLoading,
    error: queryError,
    refetch
  } = useProfileQuery({ enabled: fetchOnMount });

  const profile: UserProfile | null = useMemo(() => {
    if (!profileResponse) return null;
    return 'user' in profileResponse
      ? profileResponse.user
      : (profileResponse as UserProfile);
  }, [profileResponse]);

  const error = mutationError ?? (queryError ? queryError.message : null);
  const isLoading = isQueryLoading || isMutating;

  // Fire success/error callbacks when the shared query resolves.
  useEffect(() => {
    if (profile) onSuccess?.(profile);
  }, [profile, onSuccess]);
  useEffect(() => {
    if (queryError) onError?.(queryError.message);
  }, [queryError, onError]);

  const fetchProfile = useCallback(async () => {
    const result = await refetch();
    if (!result.data) return null;
    return 'user' in result.data
      ? result.data.user
      : (result.data as UserProfile);
  }, [refetch]);

  // Sync the shared cache after mutations so every subscriber re-renders
  // with the new user object — no second `/profile` fetch needed.
  const writeUserToCache = useCallback(
    (next: UserProfile) => {
      queryClient.setQueryData<ProfileResponse>(profileKeys.user(), (prev) =>
        prev ? { ...prev, user: next } : prev
      );
    },
    [queryClient]
  );

  const updateProfile = useCallback(
    async (data: UpdateUserProfileRequest) => {
      setIsMutating(true);
      setMutationError(null);
      try {
        const result = await updateUserProfileAction(data);

        if (result.success) {
          writeUserToCache(result.data);

          if (result.message) {
            toast.success(result.message);
          }

          window.dispatchEvent(
            new CustomEvent<ProfileUpdateEvent>('profileUpdate', {
              detail: {
                type: 'user_profile_updated',
                userId: result.data.id.toString(),
                changes: data,
                timestamp: new Date().toISOString()
              }
            })
          );

          router.refresh();
          return result.data;
        }

        const errorMsg = result.error || 'Failed to update profile';
        setMutationError(errorMsg);
        toast.error(errorMsg);

        if (result.validationErrors) {
          Object.entries(result.validationErrors).forEach(
            ([field, message]) => {
              toast.error(`${field}: ${message}`);
            }
          );
        }

        return null;
      } catch (_err) {
        const errorMsg = 'An unexpected error occurred';
        setMutationError(errorMsg);
        toast.error(errorMsg);
        return null;
      } finally {
        setIsMutating(false);
      }
    },
    [router, writeUserToCache]
  );

  const uploadAvatar = useCallback(
    async (file: File) => {
      const result = await uploadProfilePhotoAction(file);

      if (result.success && result.data) {
        if (profile) {
          writeUserToCache({ ...profile, avatar: result.data.photoUrl });
        }

        if (result.message) {
          toast.success(result.message);
        }

        router.refresh();
        return result.data;
      }

      toast.error(
        'error' in result && result.error
          ? result.error
          : 'Failed to upload avatar'
      );
      return null;
    },
    [profile, router, writeUserToCache]
  );

  const uploadSignature = useCallback(async (_file: File) => {
    toast.error('Signature upload is not implemented yet');
    return null;
  }, []);

  const refreshProfile = useCallback(async () => {
    return fetchProfile();
  }, [fetchProfile]);

  const displayName = useMemo(
    () => (profile ? getUserDisplayName(profile) : ''),
    [profile]
  );

  const initials = useMemo(
    () => (profile ? getUserInitials(profile) : ''),
    [profile]
  );

  // Listen for profile update events from other components so cross-tab
  // / cross-component edits still refresh the local view.
  useEffect(() => {
    const handleProfileUpdate = (event: CustomEvent<ProfileUpdateEvent>) => {
      if (event.detail.type === 'user_profile_updated') {
        refetch();
      }
    };

    window.addEventListener(
      'profileUpdate',
      handleProfileUpdate as EventListener
    );
    return () => {
      window.removeEventListener(
        'profileUpdate',
        handleProfileUpdate as EventListener
      );
    };
  }, [refetch]);

  return {
    profile,
    isLoading,
    error,
    displayName,
    initials,
    fetchProfile,
    updateProfile,
    uploadAvatar,
    uploadSignature,
    refreshProfile
  };
}

// ============================================================================
// Law Firm Profile Hook
// ============================================================================

interface UseLawFirmProfileOptions {
  fetchOnMount?: boolean;
  useCache?: boolean;
  cacheDuration?: number;
  onSuccess?: (profile: LawFirmProfile) => void;
  onError?: (error: string) => void;
}

export function useLawFirmProfile(options: UseLawFirmProfileOptions = {}) {
  const {
    fetchOnMount = true,
    useCache = true,
    cacheDuration = 5 * 60 * 1000,
    onSuccess,
    onError
  } = options;

  const router = useRouter();
  const [profile, setProfile] = useState<LawFirmProfile | null>(null);
  const [isLoading, setIsLoading] = useState(fetchOnMount);
  const [error, setError] = useState<string | null>(null);

  // Fetch profile data
  const fetchProfile = useCallback(async () => {
    // Check cache first
    if (useCache) {
      const cached = getCachedProfile<LawFirmProfile>('lawFirm');
      if (cached) {
        setProfile(cached);
        setIsLoading(false);
        return cached;
      }
    }

    setIsLoading(true);
    setError(null);

    try {
      // Law firm profile is not separate in this implementation
      // For now, return a mock law firm profile
      const result:
        | { success: true; data: LawFirmProfile }
        | { success: false; error: string } = {
        success: true as const,
        data: mockLawFirmProfile
      };

      if (result.success) {
        setProfile(result.data);

        // Cache the result
        if (useCache) {
          setCachedProfile('lawFirm', result.data, cacheDuration);
        }

        onSuccess?.(result.data);
        return result.data;
      } else {
        const errorMsg = 'Failed to load law firm profile';
        setError(errorMsg);
        onError?.(errorMsg);
        return null;
      }
    } catch (_err) {
      const errorMsg = 'An unexpected error occurred';
      setError(errorMsg);
      onError?.(errorMsg);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [useCache, cacheDuration, onSuccess, onError]);

  // Update profile data
  const updateProfile = useCallback(
    async (data: UpdateLawFirmProfileRequest) => {
      setIsLoading(true);
      setError(null);

      try {
        // Law firm profile update is not separate in this implementation
        // For now, return the existing profile
        const result = {
          success: true as const,
          data: profile || mockLawFirmProfile,
          message: 'Law firm profile update not implemented'
        };

        if (result.success) {
          setProfile(result.data);

          // Update cache
          if (useCache) {
            setCachedProfile('lawFirm', result.data, cacheDuration);
          }

          // Show success message
          if (result.message) {
            toast.success(result.message);
          }

          // Trigger profile update event
          window.dispatchEvent(
            new CustomEvent<ProfileUpdateEvent>('profileUpdate', {
              detail: {
                type: 'firm_profile_updated',
                firmId: result.data.id,
                changes: data,
                timestamp: new Date().toISOString()
              }
            })
          );

          // Refresh the page to update any server components
          router.refresh();

          return result.data;
        } else {
          const errorMsg = 'Failed to update law firm profile';
          setError(errorMsg);
          toast.error(errorMsg);

          // Show validation errors if any
          // (validation errors handling removed due to type issues)

          return null;
        }
      } catch (_err) {
        const errorMsg = 'An unexpected error occurred';
        setError(errorMsg);
        toast.error(errorMsg);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [useCache, cacheDuration, router, profile]
  );

  // Refresh profile (clear cache and refetch)
  const refreshProfile = useCallback(async () => {
    clearCachedProfile('lawFirm');
    return fetchProfile();
  }, [fetchProfile]);

  // Fetch on mount if enabled
  useEffect(() => {
    if (fetchOnMount) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- the effect calls fetchProfile, whose setStates all sit behind an await; nothing runs synchronously with the effect body.
      fetchProfile();
    }
  }, [fetchOnMount, fetchProfile]);

  // Listen for profile update events from other components
  useEffect(() => {
    const handleProfileUpdate = (event: CustomEvent<ProfileUpdateEvent>) => {
      if (event.detail.type === 'firm_profile_updated' && profile) {
        // Refresh profile data if it was updated elsewhere
        refreshProfile();
      }
    };

    window.addEventListener(
      'profileUpdate',
      handleProfileUpdate as EventListener
    );
    return () => {
      window.removeEventListener(
        'profileUpdate',
        handleProfileUpdate as EventListener
      );
    };
  }, [profile, refreshProfile]);

  return {
    profile,
    isLoading,
    error,
    fetchProfile,
    updateProfile,
    refreshProfile
  };
}

// ============================================================================
// Current User ID Hook (React Query version)
// ============================================================================

import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { gatedAction } from '@/lib/api/auth-gate';
import { getCurrentUserIdAction } from './actions';
import type { ProfileResponse } from './types';
import type { ThemeChoice } from '@/lib/utils/theme';

interface UseCurrentUserIdOptions {
  /**
   * Whether the query is enabled
   * @default true
   */
  enabled?: boolean;

  /**
   * Time in milliseconds to consider data fresh
   * @default 300000 (5 minutes)
   */
  staleTime?: number;

  /**
   * Callback when user ID is successfully loaded
   */
  onSuccess?: (userId: number) => void;

  /**
   * Callback when loading fails
   */
  onError?: (error: string) => void;
}

/**
 * Lightweight hook to get only the current user's ID using React Query
 * Use this instead of useUserProfile when you only need the user ID
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { userId, isLoading, error } = useCurrentUserId();
 *
 *   if (isLoading) return <Spinner />;
 *   if (!userId) return null;
 *
 *   return <div>User ID: {userId}</div>;
 * }
 * ```
 */
export function useCurrentUserId(options: UseCurrentUserIdOptions = {}) {
  const {
    enabled = true,
    staleTime = 5 * 60 * 1000, // 5 minutes
    onSuccess,
    onError: _onError
  } = options;

  const query = useQuery({
    queryKey: ['current-user-id'],
    queryFn: async () => {
      const data = await gatedAction(() => getCurrentUserIdAction());
      onSuccess?.(data);
      return data;
    },
    enabled,
    staleTime,
    retry: 1
  });

  return {
    userId: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error?.message ?? null,
    refetch: query.refetch
  };
}

// ============================================================================
// Profile Query Hook (React Query + directApiClient)
// ============================================================================

interface UseProfileQueryOptions {
  /**
   * Whether the query is enabled
   * @default true
   */
  enabled?: boolean;

  /**
   * Time in milliseconds to consider data fresh
   * @default 300000 (5 minutes)
   */
  staleTime?: number;
}

/**
 * Single source of truth for the `/profile` fetch.
 *
 * Every consumer that needs the current user, permissions, features, or
 * banners must subscribe via this hook so TanStack Query deduplicates
 * the network call (one fetch per dashboard mount, regardless of how
 * many providers/components subscribe).
 *
 * The query is keyed on `profileKeys.user()` and the queryFn is
 * `gatedAction(getUserProfileAction)` so auth-refresh behaviour matches
 * direct server-action callers.
 */
export function useProfileQuery(options: UseProfileQueryOptions = {}) {
  const { enabled = true, staleTime = 5 * 60 * 1000 } = options;

  return useQuery({
    queryKey: profileKeys.user(),
    queryFn: async (): Promise<ProfileResponse> => {
      return gatedAction(() => getUserProfileAction());
    },
    enabled,
    staleTime,
    retry: 1
  });
}

// ============================================================================
// Current User Email Hook
// ============================================================================

/**
 * The signed-in user's email address, read from the canonical `/profile`
 * query.
 *
 * This is the identity source for every "is that me?" comparison in the app:
 * the Outlook send-identity gates (suppressing "Send me a copy" when the
 * viewer *is* the sending mailbox, and the first-person composer hint) and
 * client-intake's demo-matter detection. It subscribes to `useProfileQuery`,
 * so it shares the dashboard's single `/profile` fetch rather than adding a
 * request of its own.
 *
 * `email` is `null` only while the profile is in flight or genuinely
 * unavailable — consult `isLoading` before reading `null` as "this is not the
 * current user".
 */
export function useCurrentUserEmail(): {
  email: string | null;
  isLoading: boolean;
} {
  const { data, isLoading } = useProfileQuery();

  const profile: UserProfile | null = data
    ? 'user' in data
      ? data.user
      : (data as UserProfile)
    : null;

  return { email: profile?.email ?? null, isLoading };
}

// ============================================================================
// Help Banner Dismissal Hook
// ============================================================================

export type HelpBannerKey = keyof DismissedHelpBanners;

const BANNER_STORAGE_KEY = 'vale:dismissed-help-banners';

function readLocalDismissals(): Partial<DismissedHelpBanners> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = localStorage.getItem(BANNER_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeLocalDismissal(bannerKey: HelpBannerKey, dismissed: boolean) {
  if (typeof window === 'undefined') return;
  try {
    const current = readLocalDismissals();
    localStorage.setItem(
      BANNER_STORAGE_KEY,
      JSON.stringify({ ...current, [bannerKey]: dismissed })
    );
  } catch {
    // localStorage may be unavailable
  }
}

function syncLocalDismissals(serverBanners?: DismissedHelpBanners) {
  if (typeof window === 'undefined' || !serverBanners) return;
  try {
    const local = readLocalDismissals();
    const merged = { ...local, ...serverBanners };
    localStorage.setItem(BANNER_STORAGE_KEY, JSON.stringify(merged));
  } catch {
    // localStorage may be unavailable
  }
}

export function useHelpBannerDismissal(bannerKey: HelpBannerKey) {
  const queryClient = useQueryClient();
  const { data: profile } = useProfileQuery();

  // Sync localStorage when server data arrives
  const serverBanners = profile?.user?.preferences?.dismissedHelpBanners;
  const serverDismissed = !!serverBanners?.[bannerKey];

  // Keep localStorage in sync with server
  if (serverBanners) {
    syncLocalDismissals(serverBanners);
  }

  // Fast synchronous check from localStorage, then server as source of truth
  const isDismissed =
    serverDismissed || readLocalDismissals()[bannerKey] === true;

  /*
    One mutation for both directions (VALE-2066): the compact banner leaves a
    small control behind when dismissed, so a dismissal is no longer one-way.
    The preference is deep-merged on the backend, so writing `false` restores
    without touching the other keys.
  */
  const dismissMutation = useMutation({
    mutationFn: async (dismissed: boolean) => {
      await updateUserPreferenceAction({
        preferences: {
          dismissedHelpBanners: { [bannerKey]: dismissed }
        }
      });
    },
    onMutate: async (dismissed: boolean) => {
      // Write to localStorage immediately
      writeLocalDismissal(bannerKey, dismissed);

      // Optimistic update
      await queryClient.cancelQueries({ queryKey: profileKeys.user() });
      const previous = queryClient.getQueryData<ProfileResponse>(
        profileKeys.user()
      );

      if (previous) {
        queryClient.setQueryData<ProfileResponse>(profileKeys.user(), {
          ...previous,
          user: {
            ...previous.user,
            preferences: {
              ...previous.user.preferences,
              dismissedHelpBanners: {
                ...previous.user.preferences?.dismissedHelpBanners,
                [bannerKey]: dismissed
              }
            }
          }
        });
      }

      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(profileKeys.user(), context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: profileKeys.user() });
    }
  });

  const dismiss = useCallback(() => {
    if (!isDismissed) {
      dismissMutation.mutate(true);
    }
  }, [isDismissed, dismissMutation]);

  const restore = useCallback(() => {
    if (isDismissed) {
      dismissMutation.mutate(false);
    }
  }, [isDismissed, dismissMutation]);

  return { isDismissed, dismiss, restore };
}

// ============================================================================
// Getting Started Page Dismissal Hook
// ============================================================================

/**
 * Reads and writes `preferences.gettingStarted.dismissed` — the user asking not
 * to be LANDED on the Getting Started screen unprompted.
 *
 * **Reversible in both directions** (VALE-1830). There was no way to un-dismiss
 * at all: `dismissed` was documented as permanent, the flag hid the sidebar
 * entry and the icon rail as well as the hub, and the hub itself bounced a
 * dismissed user to `/` — so the one route back was closed by the same click
 * that needed it. Sixteen production users were in that state. Dismiss is now
 * scoped to the two unprompted landings, the two navigation surfaces stay, and
 * `restore` undoes it from the hub.
 *
 * `onboarding.status` is invalidated alongside the profile because the flag also
 * rides on `GET /onboarding/status` as `gettingStarted.dismissed`, which is what
 * the hub and both navigation surfaces actually read.
 */
export function useGettingStartedDismissal() {
  const queryClient = useQueryClient();
  const { data: profile } = useProfileQuery();

  const isDismissed =
    profile?.user?.preferences?.gettingStarted?.dismissed === true;

  const setDismissed = useMutation({
    mutationFn: async (dismissed: boolean) => {
      const result = await updateUserPreferenceAction({
        preferences: { gettingStarted: { dismissed } }
      });
      if (!result.success) {
        throw new Error(
          result.error ||
            (dismissed
              ? 'Failed to dismiss Getting Started'
              : 'Failed to restore Getting Started')
        );
      }
    },
    onMutate: async (dismissed: boolean) => {
      await queryClient.cancelQueries({ queryKey: profileKeys.user() });
      const previous = queryClient.getQueryData<ProfileResponse>(
        profileKeys.user()
      );

      if (previous) {
        queryClient.setQueryData<ProfileResponse>(profileKeys.user(), {
          ...previous,
          user: {
            ...previous.user,
            preferences: {
              ...previous.user.preferences,
              // Spread the existing subtree: it also carries `progress`, and
              // replacing the object wholesale would drop every recorded step
              // from the optimistic copy and flash the ring back to zero.
              gettingStarted: {
                ...previous.user.preferences?.gettingStarted,
                dismissed
              }
            }
          }
        });
      }

      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(profileKeys.user(), context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: profileKeys.user() });
      queryClient.invalidateQueries({ queryKey: ['onboarding', 'status'] });
    }
  });

  return {
    isDismissed,
    dismiss: () => setDismissed.mutateAsync(true),
    restore: () => setDismissed.mutateAsync(false),
    isSaving: setDismissed.isPending
  };
}

// ============================================================================
// Theme Preference Hook
// ============================================================================

/**
 * Reads and writes `preferences.theme` — the server-side record of the user's
 * light/dark/system choice (VALE-548).
 *
 * **Why this exists at all, given the cookie.** The theme is PAINTED from a
 * cookie the server reads before any JavaScript runs (VALE-520 · DARK-3), and
 * that stays true — the `dark-theme` flag arrives asynchronously on `/profile`,
 * so nothing async can be on the first-paint path. The cookie leaves no
 * server-side trace though, and there is no product-analytics stack in this
 * repo, so without this key "is anyone actually using dark mode?" is
 * unanswerable. Cross-device persistence comes along for free but is not the
 * reason.
 *
 * **`write` is called from the toggle's own change handler and nowhere else.**
 * That is the mechanism, not merely the convention, by which an OS-preference
 * change never generates a PATCH: a `system` user whose laptop flips to dark at
 * sunset moves `resolvedTheme` without moving `theme`, and no code path here
 * observes `resolvedTheme` at all.
 */
export function useThemePreference(): {
  /** The stored choice, or `undefined` when the profile has not been read. */
  stored: ThemeChoice | undefined;
  isLoading: boolean;
  /**
   * Whether `/profile` actually returned. **Check this before reading `stored`**
   * (VALE-1164): `isLoading` alone does not cover a profile whose fetch FAILED,
   * because react-query reports `isLoading: false` with `data: undefined` once
   * the retry is exhausted. A caller that treated that as "no theme stored" would
   * see it differ from the browser's choice and write on every single load, with
   * nothing to converge against.
   */
  loaded: boolean;
  /** Persist an EXPLICIT user choice. Never call this from an effect on `resolvedTheme`. */
  write: (choice: ThemeChoice) => void;
} {
  const queryClient = useQueryClient();
  const { data: profile, isLoading } = useProfileQuery();

  const mutation = useMutation({
    // Serialised, and this is load-bearing rather than tidy. Two writes can be in
    // flight within a few hundred milliseconds — the mount-time reconcile,
    // followed by a user who reaches for the toggle straight away — and without a
    // scope react-query runs them concurrently, so the EARLIER choice can land
    // last and win. Browser verification caught exactly that: a reconcile PATCH
    // overtook the `dark` the user had just picked, leaving the profile on the
    // value they had moved away from. A scope makes same-scope mutations strictly
    // FIFO, so the last choice made is the last one written.
    scope: { id: 'profile-theme-preference' },
    mutationFn: async (choice: ThemeChoice) => {
      const result = await updateUserPreferenceAction({
        preferences: { theme: choice }
      });
      if (!result.success) {
        throw new Error(result.error || 'Failed to save theme preference');
      }
    },
    onMutate: async (choice: ThemeChoice) => {
      await queryClient.cancelQueries({ queryKey: profileKeys.user() });
      const previous = queryClient.getQueryData<ProfileResponse>(
        profileKeys.user()
      );

      if (previous) {
        queryClient.setQueryData<ProfileResponse>(profileKeys.user(), {
          ...previous,
          user: {
            ...previous.user,
            preferences: { ...previous.user.preferences, theme: choice }
          }
        });
      }

      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(profileKeys.user(), context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: profileKeys.user() });
    }
  });

  // `mutate` (not `mutation`) is the dependency on purpose: react-query keeps
  // `mutate` referentially stable across renders while the mutation object
  // itself is new every render, so depending on the object would give callers an
  // unstable `write` and re-run their effects on every render.
  //
  // Deliberately silent on failure beyond the cache revert: the theme has
  // already changed locally and is already in the cookie, so the user's screen
  // is correct either way. A toast for a failed preference write would be noise.
  const { mutate } = mutation;
  const write = useCallback(
    (choice: ThemeChoice) => {
      mutate(choice);
    },
    [mutate]
  );

  return {
    stored: profile?.user?.preferences?.theme,
    isLoading,
    loaded: profile !== undefined,
    write
  };
}

/**
 * **"I don't sign letters"** (VALE-1633), the correspondence-setup flow's way
 * for an assistant or paralegal to say a handwritten signature is not theirs to
 * give. Stored server-side on the user's preferences, so the sign-in prompt
 * (`GET /onboarding/status`, `promptRequired`) stops asking for one. Reversible.
 *
 * Optimistic on the profile query, like the help-banner dismissal above, so the
 * screen answers at once; the invalidation afterwards is the `profile` bundle,
 * which also refreshes the onboarding status.
 */
export function useDoesNotSignLetters() {
  const queryClient = useQueryClient();
  const { data: profile } = useProfileQuery();
  const doesNotSignLetters =
    profile?.user?.preferences?.onboarding?.doesNotSignLetters === true;

  const mutation = useMutation({
    mutationFn: async (value: boolean) => {
      const result = await updateUserPreferenceAction({
        preferences: { onboarding: { doesNotSignLetters: value } }
      });
      if (!result.success) {
        throw new Error(result.error || 'Failed to save your choice');
      }
    },
    onMutate: async (value: boolean) => {
      await queryClient.cancelQueries({ queryKey: profileKeys.user() });
      const previous = queryClient.getQueryData<ProfileResponse>(
        profileKeys.user()
      );
      if (previous) {
        queryClient.setQueryData<ProfileResponse>(profileKeys.user(), {
          ...previous,
          user: {
            ...previous.user,
            preferences: {
              ...previous.user.preferences,
              onboarding: {
                ...previous.user.preferences?.onboarding,
                doesNotSignLetters: value
              }
            }
          }
        });
      }
      return { previous };
    },
    onError: (_error, _value, context) => {
      if (context?.previous) {
        queryClient.setQueryData(profileKeys.user(), context.previous);
      }
    },
    onSettled: () => {
      for (const queryKey of getDependencies('profile')) {
        void queryClient.invalidateQueries({ queryKey });
      }
    }
  });

  return {
    doesNotSignLetters,
    setDoesNotSignLetters: mutation.mutateAsync,
    isSaving: mutation.isPending
  };
}
